import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { apiEndpoint } from "../../utils/env";
import { createDatabase, addDatabaseIndexes } from "../../api/adminApi";

const DRIVER_TABS = [
  { id: "none",  label: "None" },
  { id: "afp",   label: "Driver AFP" },
  { id: "pdf",   label: "Driver PDF" },
  { id: "meta",  label: "JSON / CSV" },
];

// Fields that are structural/control in JSON/CSV index files and should not
// become user-defined index columns (they are handled by the pipeline itself).
const CONTROL_FIELDS = new Set(["pagecount", "documentname"]);

// Client-side parse of a CSV or JSON metadata file into field names.
// TODO: replace/augment with a backend endpoint once server-side metadata
//       parsing (type coercion, schema validation, etc.) is implemented.
function parseMetaFileClientSide(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(new Error("Could not read file."));
    reader.onload = (e) => {
      const text = (e.target.result || "").trim();
      try {
        if (file.name.toLowerCase().endsWith(".json")) {
          const parsed = JSON.parse(text);
          let keys = [];
          if (Array.isArray(parsed) && parsed.length > 0 && typeof parsed[0] === "object") {
            keys = Object.keys(parsed[0]);
          } else if (typeof parsed === "object" && parsed !== null) {
            keys = Object.keys(parsed);
          }
          keys = keys.filter((k) => !CONTROL_FIELDS.has(k.toLowerCase()));
          if (keys.length === 0) throw new Error("No index fields found in JSON file.");
          resolve(keys.map((k) => ({ name: k, sampleValues: [] })));
        } else {
          // CSV — first non-empty line is the header row
          const firstLine = text.split(/\r?\n/).find((l) => l.trim());
          if (!firstLine) throw new Error("CSV file appears to be empty.");
          const headers = firstLine
            .split(",")
            .map((h) => h.trim().replace(/^["']|["']$/g, ""))
            .filter((h) => h && !CONTROL_FIELDS.has(h.toLowerCase()));
          if (headers.length === 0) throw new Error("No index columns found in CSV.");
          resolve(headers.map((h) => ({ name: h, sampleValues: [] })));
        }
      } catch (err) {
        reject(err);
      }
    };
    reader.readAsText(file);
  });
}

export default function CreateDatabaseModal({ token, onClose, onCreated }) {
  const navigate = useNavigate();
  const [step, setStep] = useState("choose"); // "choose" | 1 | 2 | "done"

  // Step 1 state
  const [dbName, setDbName]             = useState("");
  const [dbNameError, setDbNameError]   = useState("");
  const [driverTab, setDriverTab]       = useState("none"); // "none" | "afp" | "pdf" | "meta"
  const [partitionByMonth, setPartitionByMonth] = useState(false);
  const [afpFile, setAfpFile]           = useState(null);
  const [pdfFile, setPdfFile]           = useState(null);
  const [metaFile, setMetaFile]         = useState(null);
  const [busy, setBusy]                 = useState(false);
  const [error, setError]               = useState("");

  // Step 2 state — unified field list regardless of source
  const [fields, setFields]       = useState([]); // [{ name, sampleValues }]
  const [selected, setSelected]   = useState(new Set());
  const [renames, setRenames]     = useState({}); // { [originalName]: displayName }
  const [fieldError, setFieldError] = useState("");
  const [fieldBusy, setFieldBusy]   = useState(false);
  const [fieldSource, setFieldSource] = useState(""); // "afp" | "pdf" | "meta" — for display

  // Completion
  const [createdDbId, setCreatedDbId]           = useState(null);
  const [indexesAddedCount, setIndexesAddedCount] = useState(0);

  // Locked-down (DML-only) mode: create returns a provisioning script for the DBA
  // instead of creating the table.
  const [pendingScript, setPendingScript]       = useState("");
  // True once a create came back Pending — the base + fields must be provisioned by a DBA.
  // We still run driver detection (a file parse, no table needed) so the user can pick fields;
  // the picked fields' ALTERs are appended to the table CREATE for a single combined script.
  const [lockdownPending, setLockdownPending]   = useState(false);
  const [createScript, setCreateScript]         = useState(""); // table CREATE portion, kept to combine with field ALTERs

  const afpInputRef  = useRef(null);
  const pdfInputRef  = useRef(null);
  const metaInputRef = useRef(null);

  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);

  // When we enter step 2, trigger the appropriate detection
  useEffect(() => {
    if (step !== 2) return;
    if (fieldSource === "afp") detectAfpFields();
    else if (fieldSource === "pdf") detectPdfFields();
    else if (fieldSource === "meta") parseMetaFields();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step]);

  function validateDbName(name) {
    if (!name || !name.trim()) return "Database name is required.";
    if (!/^[A-Za-z0-9_]+$/.test(name)) return "Only letters, digits and underscores are allowed.";
    if (name.length > 50) return "Name must be 50 characters or fewer.";
    return "";
  }

  // Step 1 → Performance page: validate name + driver, then advance.
  function goToPerf() {
    const v = validateDbName(dbName);
    if (v) { setDbNameError(v); return; }
    if (driverTab === "afp"  && !afpFile)  { setError("Choose an AFP file or switch to None."); return; }
    if (driverTab === "pdf"  && !pdfFile)  { setError("Choose a PDF file or switch to None."); return; }
    if (driverTab === "meta" && !metaFile) { setError("Choose a JSON or CSV file or switch to None."); return; }
    setDbNameError("");
    setError("");
    setStep("perf");
  }

  async function handleCreate() {
    const v = validateDbName(dbName);
    if (v) { setDbNameError(v); return; }
    if (driverTab === "afp"  && !afpFile)  { setError("Choose an AFP file or switch to None."); return; }
    if (driverTab === "pdf"  && !pdfFile)  { setError("Choose a PDF file or switch to None."); return; }
    if (driverTab === "meta" && !metaFile) { setError("Choose a JSON or CSV file or switch to None."); return; }
    setDbNameError("");
    setError("");
    setBusy(true);
    try {
      const result = await createDatabase(token, dbName.trim(), partitionByMonth);
      if (!result?.success) {
        setError(result?.message || "Failed to create database.");
        return;
      }
      const dbId = result.dbId ?? result.id ?? null;
      setCreatedDbId(dbId);

      // Locked-down (DML-only): the table wasn't created — a DBA must run the script.
      // Detection is just a file parse (no physical table needed), so with a driver we STILL
      // go to the field-listing step; the picked fields' ALTERs get appended to this CREATE
      // for one combined provisioning script. With no driver, show the script straight away.
      if (result.pending) {
        setLockdownPending(true);
        setCreateScript(result.script || "");
        setPendingScript(result.script || "");
        if (driverTab === "none") {
          setStep("pending");
        } else {
          setFieldSource(driverTab);
          setStep(2);
        }
        return;
      }

      if (driverTab === "none") {
        setStep("done");
      } else {
        setFieldSource(driverTab);
        setStep(2);
      }
    } catch (e) {
      setError(e?.message || "Failed to create database.");
    } finally {
      setBusy(false);
    }
  }

  async function detectAfpFields() {
    setFieldBusy(true);
    setFieldError("");
    setFields([]);
    setRenames({});
    try {
      const formData = new FormData();
      formData.append("file", afpFile);
      const url = apiEndpoint("/api/Upload/ParseIndexHints");
      const resp = await fetch(url, {
        method: "POST",
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        body: formData,
      });
      if (!resp.ok) {
        const err = await resp.json().catch(() => ({}));
        throw new Error(err?.message || `HTTP ${resp.status}`);
      }
      const data = await resp.json();
      if (data.fields && data.fields.length > 0) {
        setFields(data.fields);
        setSelected(new Set(data.fields.map((f) => f.name)));
      } else {
        setFieldError(
          data.note ||
          (data.tleRecordCount === 0
            ? "No TLE records found in this AFP. You can add indexes manually from the Indexes page."
            : `${data.tleRecordCount} TLE record(s) found but no readable field names extracted.`)
        );
      }
    } catch (e) {
      setFieldError(e.message || "AFP scanning failed.");
    } finally {
      setFieldBusy(false);
    }
  }

  async function detectPdfFields() {
    setFieldBusy(true);
    setFieldError("");
    setFields([]);
    setRenames({});
    try {
      const formData = new FormData();
      formData.append("file", pdfFile);
      const url = apiEndpoint("/api/Upload/ParseIndexHints");
      const resp = await fetch(url, {
        method: "POST",
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        body: formData,
      });
      if (!resp.ok) {
        const err = await resp.json().catch(() => ({}));
        throw new Error(err?.message || `HTTP ${resp.status}`);
      }
      const data = await resp.json();
      if (data.fields && data.fields.length > 0) {
        setFields(data.fields);
        setSelected(new Set(data.fields.map((f) => f.name)));
      } else {
        setFieldError(
          data.note ||
          "No PieceInfo fields found in this PDF. You can add indexes manually from the Indexes page."
        );
      }
    } catch (e) {
      setFieldError(e.message || "PDF scanning failed.");
    } finally {
      setFieldBusy(false);
    }
  }

  async function parseMetaFields() {
    setFieldBusy(true);
    setFieldError("");
    setFields([]);
    setRenames({});
    try {
      const parsed = await parseMetaFileClientSide(metaFile);
      setFields(parsed);
      setSelected(new Set(parsed.map((f) => f.name)));
    } catch (e) {
      setFieldError(e.message || "Could not parse the file.");
    } finally {
      setFieldBusy(false);
    }
  }

  function toggleField(name) {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(name)) next.delete(name);
      else next.add(name);
      return next;
    });
  }

  async function handleAddIndexes() {
    if (!createdDbId) { setFieldError("Database ID unavailable. Please close and retry."); return; }
    const chosen = fields.filter((f) => selected.has(f.name));
    if (chosen.length === 0) { setFieldError("Select at least one field, or click Skip."); return; }
    setFieldBusy(true);
    setFieldError("");
    try {
      const result = await addDatabaseIndexes(token, createdDbId, chosen.map((f, i) => ({
        fieldName: f.name.replace(/[^A-Za-z0-9_]/g, "_"),
        fieldDisplayName: (renames[f.name] || f.name).trim() || f.name,
        dataType: "string",
        length: 200,
        isShownOnReport: true,
        isActive: true,
        isDeletable: true,
        fieldOrder: i,
      })));
      if (result?.success === false) {
        setFieldError(result.message || "Failed to add indexes.");
        return;
      }
      setIndexesAddedCount(chosen.length);
      // Locked-down: the fields are registered but their columns are deferred to the DBA. Combine
      // the table CREATE with the returned field ALTERs into one script and show the pending step.
      if (lockdownPending) {
        const alterScript = result?.script || "";
        setPendingScript([createScript, alterScript].filter(Boolean).join("\n\n"));
        setStep("pending");
        return;
      }
      setStep("done");
    } catch (e) {
      setFieldError(e?.message || "Failed to add indexes.");
    } finally {
      setFieldBusy(false);
    }
  }

  function handleSkipIndexes() {
    setIndexesAddedCount(0);
    // Locked-down with no fields chosen: still need the table CREATE script provisioned.
    if (lockdownPending) {
      setPendingScript(createScript);
      setStep("pending");
      return;
    }
    setStep("done");
  }

  function handleDone() {
    onCreated(createdDbId);
  }

  function copyScript() {
    if (navigator.clipboard?.writeText) navigator.clipboard.writeText(pendingScript);
  }

  function downloadScript() {
    const blob = new Blob([pendingScript], { type: "application/sql" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `provision_${dbName || "repository"}.sql`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }

  // ── helpers ──────────────────────────────────────────────────────────────
  const driverFile = driverTab === "afp" ? afpFile : driverTab === "pdf" ? pdfFile : driverTab === "meta" ? metaFile : null;
  const hasDriver  = !!driverFile;
  const step2Title =
    fieldSource === "afp" ? "TLE Fields Detected" :
    fieldSource === "pdf" ? "PieceInfo Fields Detected" :
    "Metadata Fields";
  const step2Scanning =
    fieldSource === "afp" ? "Scanning AFP for TLE fields…" :
    fieldSource === "pdf" ? "Scanning PDF for PieceInfo fields…" :
    "Parsing metadata file…";

  return (
    <div className="dbModalOverlay" onClick={onClose}>
      <div
        className="dbModalCard createDbModalCard"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          className="drawer-close-btn drawer-close-btn-abs"
          onClick={onClose}
          aria-label="Close"
          title="Close"
        >
          <svg width="8" height="8" viewBox="0 0 8 8" fill="none" aria-hidden="true">
            <line x1="1" y1="1" x2="7" y2="7" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round"/>
            <line x1="7" y1="1" x2="1" y2="7" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round"/>
          </svg>
        </button>
        {/* ── Step 0: Choose setup method ── */}
        {step === "choose" && (
          <>
            <div className="dbModalIcon" style={{ color: "#c47d00" }}>+</div>
            <div className="dbModalTitle">Create Database</div>
            <div className="dbModalDesc" style={{ marginBottom: 22 }}>
              How would you like to set up the new database?
            </div>

            {/* AI wizard option */}
            <button
              type="button"
              className="createDbChoiceCard createDbChoiceCardPrimary"
              onClick={() => { onClose(); navigate("/?wizard=1"); }}
            >
              <span className="createDbChoiceIcon">✦</span>
              <span className="createDbChoiceBody">
                <span className="createDbChoiceTitle">AI-assisted setup</span>
                <span className="createDbChoiceDesc">
                  Let the AI guide you through naming, describing and defining index fields
                  for your database using a conversational interface.
                </span>
              </span>
              <span className="createDbChoiceArrow">→</span>
            </button>

            {/* Manual option */}
            <button
              type="button"
              className="createDbChoiceCard"
              onClick={() => setStep(1)}
            >
              <span className="createDbChoiceIcon" style={{ color: "#555" }}>⊟</span>
              <span className="createDbChoiceBody">
                <span className="createDbChoiceTitle" style={{ color: "#333" }}>Manual setup</span>
                <span className="createDbChoiceDesc">
                  Enter a name, optionally provide a driver AFP, PDF (PieceInfo), or metadata file
                  to auto-detect index fields, and configure everything yourself.
                </span>
              </span>
              <span className="createDbChoiceArrow" style={{ color: "#aaa" }}>→</span>
            </button>

            <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 18 }}>
              <button type="button" className="dbModalCancel" onClick={onClose}>Cancel</button>
            </div>
          </>
        )}

        {/* ── Step 1: Setup ── */}
        {step === 1 && (
          <>
            <div className="dbModalIcon">+</div>
            <div className="dbModalTitle">Create Database</div>
            <div className="dbModalDesc" style={{ marginBottom: 20 }}>
              Define the database name and optionally provide a driver file to
              auto-detect index fields. The driver file will <strong>not</strong> be archived.
            </div>

            {/* DB name */}
            <div style={{ marginBottom: 16 }}>
              <label className="createDbLabel">Database name</label>
              <input
                autoFocus
                type="text"
                value={dbName}
                onChange={(e) => { setDbName(e.target.value); setDbNameError(""); }}
                onKeyDown={(e) => { if (e.key === "Enter") goToPerf(); }}
                placeholder="e.g. AKBANK_STATEMENTS"
                className={`createDbInput${dbNameError ? " createDbInputError" : ""}`}
              />
              {dbNameError && <div className="createDbFieldErr">{dbNameError}</div>}
            </div>

            {/* Driver file section */}
            <div style={{ marginBottom: 20 }}>
              <label className="createDbLabel">
                Driver file source{" "}
                <span style={{ color: "#888", fontWeight: 400 }}>(optional)</span>
              </label>

              {/* Tab selector */}
              <div className="createDbTabBar">
                {DRIVER_TABS.map((t) => (
                  <button
                    key={t.id}
                    type="button"
                    className={`createDbTab${driverTab === t.id ? " createDbTabActive" : ""}`}
                    onClick={() => { setDriverTab(t.id); setError(""); }}
                  >
                    {t.label}
                  </button>
                ))}
              </div>

              {/* None */}
              {driverTab === "none" && (
                <p className="createDbTabNote">
                  No driver file — the database will be created with no custom index columns.
                  You can add indexes manually from this page at any time.
                </p>
              )}

              {/* AFP */}
              {driverTab === "afp" && (
                <div className="createDbFilePicker">
                  <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                    <button
                      type="button"
                      className="icon-btn"
                      style={{ fontSize: 12, padding: "5px 12px", whiteSpace: "nowrap" }}
                      onClick={() => afpInputRef.current?.click()}
                    >
                      Choose AFP
                    </button>
                    <span className={`createDbFileName${afpFile ? "" : " createDbFileNameEmpty"}`}>
                      {afpFile ? afpFile.name : "No file chosen"}
                    </span>
                    {afpFile && (
                      <button type="button" className="link-btn" style={{ color: "#888" }}
                        onClick={() => setAfpFile(null)}>×</button>
                    )}
                  </div>
                  <input ref={afpInputRef} type="file" accept=".afp" style={{ display: "none" }}
                    onChange={(e) => { setAfpFile(e.target.files?.[0] || null); e.target.value = ""; }} />
                  <p className="createDbTabNote" style={{ marginTop: 6, marginBottom: 0 }}>
                    Tagged Logical Element (TLE) records will be scanned to suggest index fields.
                  </p>
                </div>
              )}

              {/* PDF PieceInfo */}
              {driverTab === "pdf" && (
                <div className="createDbFilePicker">
                  <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                    <button
                      type="button"
                      className="icon-btn"
                      style={{ fontSize: 12, padding: "5px 12px", whiteSpace: "nowrap" }}
                      onClick={() => pdfInputRef.current?.click()}
                    >
                      Choose PDF
                    </button>
                    <span className={`createDbFileName${pdfFile ? "" : " createDbFileNameEmpty"}`}>
                      {pdfFile ? pdfFile.name : "No file chosen"}
                    </span>
                    {pdfFile && (
                      <button type="button" className="link-btn" style={{ color: "#888" }}
                        onClick={() => setPdfFile(null)}>×</button>
                    )}
                  </div>
                  <input ref={pdfInputRef} type="file" accept=".pdf" style={{ display: "none" }}
                    onChange={(e) => { setPdfFile(e.target.files?.[0] || null); e.target.value = ""; }} />
                  <p className="createDbTabNote" style={{ marginTop: 6, marginBottom: 0 }}>
                    PieceInfo (IBM-ODIndexes) fields embedded per-page will be scanned to suggest index fields.
                  </p>
                </div>
              )}

              {/* JSON / CSV */}
              {driverTab === "meta" && (
                <div className="createDbFilePicker">
                  <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                    <button
                      type="button"
                      className="icon-btn"
                      style={{ fontSize: 12, padding: "5px 12px", whiteSpace: "nowrap" }}
                      onClick={() => metaInputRef.current?.click()}
                    >
                      Choose file
                    </button>
                    <span className={`createDbFileName${metaFile ? "" : " createDbFileNameEmpty"}`}>
                      {metaFile ? metaFile.name : "No file chosen"}
                    </span>
                    {metaFile && (
                      <button type="button" className="link-btn" style={{ color: "#888" }}
                        onClick={() => setMetaFile(null)}>×</button>
                    )}
                  </div>
                  <input ref={metaInputRef} type="file" accept=".json,.csv" style={{ display: "none" }}
                    onChange={(e) => { setMetaFile(e.target.files?.[0] || null); e.target.value = ""; }} />
                  <p className="createDbTabNote" style={{ marginTop: 6, marginBottom: 0 }}>
                    Column headers (CSV) or top-level keys (JSON) will be used as index field names.{" "}
                    <span style={{ color: "#b45309" }}>
                      Server-side metadata parsing is coming soon — fields are extracted locally for now.
                    </span>
                  </p>
                </div>
              )}
            </div>

            {error && <div className="createDbFieldErr" style={{ marginBottom: 12 }}>{error}</div>}

            <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
              <button type="button" className="dbModalCancel" onClick={onClose}>Cancel</button>
              <button type="button" className="primary-btn" onClick={goToPerf}>
                Next →
              </button>
            </div>
          </>
        )}

        {/* ── Step "perf": Performance / partitioning ── */}
        {step === "perf" && (
          <>
            <div className="dbModalIcon">▤</div>
            <div className="dbModalTitle">Performance</div>
            <div className="dbModalDesc" style={{ marginBottom: 22 }}>
              How should <strong>{dbName}</strong> store data for scale? Optional, and set
              once — this can’t be changed after the base is created.
            </div>

            <div
              className={`createDbOption${partitionByMonth ? " is-on" : ""}`}
              role="button"
              tabIndex={0}
              aria-pressed={partitionByMonth}
              onClick={() => setPartitionByMonth((v) => !v)}
              onKeyDown={(e) => {
                if (e.key === " " || e.key === "Enter") { e.preventDefault(); setPartitionByMonth((v) => !v); }
              }}
            >
              <div className="createDbOptionIcon" aria-hidden="true">▤</div>
              <div className="createDbOptionBody">
                <div className="createDbOptionTitle">Partition by month</div>
                <div className="createDbOptionDesc">
                  Keeps searches fast and retention cheap as a high-volume base
                  (millions of documents) grows — recommended for statement, bill and
                  invoice archives. Falls back to a standard table if the database
                  doesn’t support partitioning.
                </div>
              </div>
              <span className={`createDbSwitch${partitionByMonth ? " is-on" : ""}`} aria-hidden="true">
                <span className="createDbSwitchThumb" />
              </span>
            </div>

            {error && <div className="createDbFieldErr" style={{ marginTop: 16 }}>{error}</div>}

            <div style={{ display: "flex", gap: 10, justifyContent: "space-between", marginTop: 24 }}>
              <button type="button" className="dbModalCancel" onClick={() => { setError(""); setStep(1); }}>
                ← Back
              </button>
              <button type="button" className="primary-btn" disabled={busy} onClick={handleCreate}>
                {busy ? "Creating…" : hasDriver ? "Create & Detect Fields →" : "Create Database"}
              </button>
            </div>
          </>
        )}

        {/* ── Step 2: Field selection ── */}
        {step === 2 && (
          <>
            <div className="dbModalIcon">⊞</div>
            <div className="dbModalTitle">{step2Title}</div>
            <div className="dbModalDesc" style={{ marginBottom: 14 }}>
              Database <strong>{dbName}</strong> created. Select the fields to add as index columns.
            </div>

            {fieldBusy && (
              <div style={{ color: "#555", fontSize: 13, marginBottom: 12 }}>{step2Scanning}</div>
            )}
            {fieldError && (
              <div className="createDbFieldErr" style={{ marginBottom: 10 }}>{fieldError}</div>
            )}

            {!fieldBusy && fields.length > 0 && (
              <>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
                  <span style={{ fontSize: 12, fontWeight: 600 }}>
                    {fields.length} field(s) detected — select to add as indexes:
                  </span>
                  <div style={{ display: "flex", gap: 10 }}>
                    <button type="button" className="link-btn" style={{ fontSize: 11 }}
                      onClick={() => setSelected(new Set(fields.map((f) => f.name)))}>
                      All
                    </button>
                    <button type="button" className="link-btn" style={{ fontSize: 11 }}
                      onClick={() => setSelected(new Set())}>
                      None
                    </button>
                  </div>
                </div>
                <div className="createDbFieldList">
                  {fields.map((f) => (
                    <div key={f.name} className="createDbFieldRow" style={{ alignItems: "center" }}>
                      <input
                        type="checkbox"
                        checked={selected.has(f.name)}
                        onChange={() => toggleField(f.name)}
                        style={{ marginTop: 0, flexShrink: 0 }}
                      />
                      <input
                        type="text"
                        value={renames[f.name] ?? f.name}
                        onChange={(e) =>
                          setRenames((prev) => ({ ...prev, [f.name]: e.target.value }))
                        }
                        disabled={!selected.has(f.name)}
                        style={{
                          flex: 1,
                          fontSize: 13,
                          fontWeight: 600,
                          border: "1px solid #d1d5db",
                          borderRadius: 5,
                          padding: "3px 8px",
                          marginLeft: 8,
                          background: selected.has(f.name) ? "#fff" : "#f3f4f6",
                          color: selected.has(f.name) ? "#111" : "#9ca3af",
                          minWidth: 0,
                        }}
                        title="Rename this field"
                      />
                      {f.sampleValues?.length > 0 && (
                        <span style={{ color: "#aaa", fontSize: 11, flexShrink: 0, marginLeft: 6 }}>
                          e.g. {f.sampleValues.slice(0, 2).join(", ")}
                        </span>
                      )}
                    </div>
                  ))}
                </div>
              </>
            )}

            <div style={{ display: "flex", gap: 10, justifyContent: "flex-end", marginTop: 16, flexWrap: "wrap" }}>
              <button type="button" className="dbModalCancel" onClick={handleSkipIndexes} disabled={fieldBusy}>
                Skip
              </button>
              <button
                type="button"
                className="primary-btn"
                disabled={fieldBusy || selected.size === 0}
                onClick={handleAddIndexes}
              >
                {fieldBusy ? "Adding…" : `Add ${selected.size} Index(es) & Finish`}
              </button>
            </div>
          </>
        )}

        {/* ── Pending: locked-down (DML-only) provisioning ── */}
        {step === "pending" && (
          <>
            <div className="dbModalIcon" style={{ color: "#c47d00" }}>◷</div>
            <div className="dbModalTitle">Awaiting DB Provisioning</div>
            <div className="dbModalDesc" style={{ marginBottom: 14 }}>
              <strong>{dbName}</strong> has been requested. This environment runs in
              locked-down mode, so the application cannot create tables. Send the script
              below to your DBA to run (connected as the owner schema).
              {indexesAddedCount > 0
                ? ` It creates the table and its ${indexesAddedCount} searchable field column(s); those fields already appear on the base and go live once you Activate.`
                : " The repository activates once its table is verified."}
            </div>
            <textarea
              readOnly
              value={pendingScript}
              onClick={(e) => e.target.select()}
              style={{
                width: "100%", height: 200, boxSizing: "border-box",
                fontFamily: "ui-monospace, Menlo, Consolas, monospace", fontSize: 12,
                border: "1px solid #d1d5db", borderRadius: 6, padding: 10, background: "#0b1021", color: "#d6e2ff",
              }}
            />
            <div style={{ display: "flex", gap: 10, justifyContent: "flex-end", marginTop: 16, flexWrap: "wrap" }}>
              <button type="button" className="dbModalCancel" onClick={copyScript}>Copy</button>
              <button type="button" className="dbModalCancel" onClick={downloadScript}>Download .sql</button>
              <button type="button" className="primary-btn" onClick={handleDone}>Done</button>
            </div>
          </>
        )}

        {/* ── Done ── */}
        {step === "done" && (
          <>
            <div className="dbModalIcon" style={{ color: "#16a34a" }}>✓</div>
            <div className="dbModalTitle">Database Created</div>
            <div className="dbModalDesc">
              <strong>{dbName}</strong> is ready.
              {indexesAddedCount > 0 && <> {indexesAddedCount} index field(s) added.</>}
            </div>
            <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 24 }}>
              <button type="button" className="primary-btn" onClick={handleDone}>Done</button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
